import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  ReferenceLine, ReferenceArea,
} from 'recharts';

// ReconciliationTile — one line per source pair showing percent delta by
// month. A shaded ±2% band marks "acceptable drift"; anything outside is
// visually obvious. This is how the reader spots WHEN sources diverged,
// not just that they do.

const GRID = { strokeDasharray: '2 4', stroke: '#141B4D', strokeOpacity: 0.1 };
const AXIS = { stroke: '#141B4D', strokeOpacity: 0.3 };
const TOOLTIP = {
  contentStyle: { background: '#FFFFFF', border: '1px solid #141B4D', borderRadius: 0, color: '#141B4D', fontSize: 12 },
};

const _reconDot = (props) => {
  const v = props.payload?.delta_pct;
  const outside = v != null && Math.abs(v) > 0.02;
  return (
    <circle cx={props.cx} cy={props.cy} r={outside ? 4 : 2.5}
      fill={outside ? '#C4162A' : '#141B4D'}
      stroke={outside ? '#FFFFFF' : 'none'} strokeWidth={outside ? 1.5 : 0} />
  );
};

export function ReconciliationTile({ pair, height = 160, testId }) {
  const data = (pair.series || []).map((p) => ({
    month: p.month,
    short: p.label ? p.label.slice(0, 3) : '',
    delta_pct: p.delta_pct,
    a: p.a, b: p.b,
    delta_abs: p.delta_abs,
  }));

  const values = data.map((d) => d.delta_pct).filter((v) => v != null);
  const maxAbs = values.length ? Math.max(0.05, ...values.map(Math.abs)) : 0.05;

  return (
    <div className="border border-indie-ink/15 bg-white p-4 flex flex-col gap-2 min-h-[220px]" data-testid={testId || `recon-${pair.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">{pair.label}</div>
        <div className="text-[10px] text-indie-ink/50">{pair.a_label} vs {pair.b_label}</div>
      </div>
      <div style={{ height }}>
        <ResponsiveContainer>
          <LineChart data={data} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
            <CartesianGrid {...GRID} />
            <XAxis dataKey="short" tick={{ fontSize: 10, fill: '#141B4D' }} {...AXIS} />
            <YAxis
              tick={{ fontSize: 10, fill: '#141B4D' }}
              width={44}
              {...AXIS}
              domain={[-maxAbs, maxAbs]}
              tickFormatter={(v) => `${(v * 100).toFixed(1)}%`}
            />
            <Tooltip
              {...TOOLTIP}
              formatter={(v, name, item) => {
                if (name === 'delta_pct') return [`${(v * 100).toFixed(2)}%`, 'Delta'];
                return [v, name];
              }}
            />
            {/* ±2% acceptable-drift band */}
            <ReferenceArea y1={-0.02} y2={0.02} fill="#5A8C3B" fillOpacity={0.08} />
            <ReferenceLine y={0} stroke="#141B4D" strokeOpacity={0.4} />
            <Line
              type="monotone"
              dataKey="delta_pct"
              stroke="#C4162A"
              strokeWidth={2}
              dot={_reconDot}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="text-[10px] text-indie-ink/50 pt-1 border-t border-indie-ink/10">
        Green band = within ±2%. Red dots exceed tolerance.
      </div>
    </div>
  );
}
